import { Options, Paging, Sorting } from "@api/models/HttpClientModels";
import BaseFilterStore from "@stores/BaseFilterStore";
import RootStore from "@stores/RootStore";
import { AxiosError } from "axios";
import { action, makeObservable, observable, runInAction } from "mobx";
import { Params } from "react-router-dom";
import Order from "../models/list/Order";
import OrderFilter from "../models/list/OrderFilter";
import OrderRepeatContainer from "../models/list/OrderRepeatContainer";
import OrderService from "../services/OrderService";
import { getIsDeliveryActive, getIsOpenTimeActive } from "@utils/restaurantHelper";

export default class OrderListViewStore extends BaseFilterStore<Order[], OrderFilter> {
    rootStore: RootStore;
    service: OrderService;

    constructor(rootStore: RootStore) {
        super(
            rootStore,
            {
                filterAction: async (filter, sorting, paging, options) => await this.findAsync(filter, sorting, paging, options),
                initializeDefaultFilter: (searchParams, params) => { this.initializeDefaultFilterParameters(searchParams, params) }
            },
            {
                orderBy: "dateCreated",
                ascending: false
            },
            {
                embed: "restaurant"
            }
        )
        this.rootStore = rootStore;
        this.service = new OrderService();

        makeObservable(this, {
            //#region UI
            isLoading: observable,
            setIsLoading: action,
            //#endregion

            orders: observable,
            setOrders: action,
            findAsync: action,

            onOrderClick: action,
            onAddToOrderCartClick: action,
            orderRepeatContainer: observable,
            setOrderRepeatContainer: action,
            isOrderDetailsDrawerOpen: observable,
            setIsOrderDetailsDrawerOpen: action,
        })
    }

    //#region UI

    isLoading: boolean = false;
    setIsLoading = (value: boolean) => {
        this.isLoading = value
    }

    //#endregion

    orders: Order[] = [];
    setOrders = (orders: Order[]) => {
        this.orders = orders;
    }

    findAsync = async (filter: OrderFilter, sorting: Sorting, paging: Paging, options: Options) => {
        this.setIsLoading(true);

        try {
            var response = (await this.service.findAsync(filter, sorting, paging, options)).data;

            this.setOrders(response.item);
            return response;
        } catch (error) {
            if (error instanceof AxiosError) {

            }
        }
        finally {
            this.setIsLoading(false);
        }
    }

    initializeDefaultFilterParameters = (searchParams: URLSearchParams, params: Readonly<Params<string>>) => {
        this.filter = {};

        var userId = params.userId;
        if (userId !== undefined) {
            this.filter.userIds = userId;
        }

        var restaurantIds = searchParams.get("restaurantIds");
        if (restaurantIds !== undefined && restaurantIds !== null) {
            this.filter.restaurantIds = restaurantIds;
        }

        var embed = searchParams.get("embed");
        if (embed !== undefined && embed !== null) {
            this.options.embed = embed
        }
    }

    onOrderClick = async (order: Order) => {
        this.setIsLoading(true);

        try {
            var orderRepeatContainer = (await this.service.getWithRecalculatedAmountAsync(order.id)).data;

            runInAction(() => {
                this.setIsOrderDetailsDrawerOpen(true)
                this.setOrderRepeatContainer(orderRepeatContainer);
            })
        } catch (error) {

        }
        finally {
            this.setIsLoading(false);
        }
    }

    onAddToOrderCartClick = () => {
        if (this.orderRepeatContainer === undefined) {
            return;
        }

        if (!this.orderRepeatContainer.canRepeat) {
            this.rootStore.toastStore.alert("info", "Opcija nije dostupna za neke narudžbe.");
            return;
        }

        const { orderFoods, restaurant } = this.orderRepeatContainer.orderWithRecalculatedAmount;
        if (orderFoods === undefined || orderFoods === null) {
            return;
        }
        if (restaurant === undefined || restaurant === null) {
            return;
        }

        restaurant.isDeliveryActive = getIsDeliveryActive(restaurant);
        restaurant.isOpenTimeActive = getIsOpenTimeActive(restaurant);

        for (var orderFood of orderFoods) {
            var foodPrice = orderFood.food.foodPrices.find(p => p.size === orderFood.foodSize);

            if (foodPrice !== undefined) {
                foodPrice.isSelected = true;
            }
        }

        this.rootStore.routerStore.navigate(`/restaurant/${restaurant.id}/offer`)

        runInAction(() => {
            this.rootStore.orderEditViewStore.clearState();
            this.rootStore.orderEditViewStore.setOrderFoods(orderFoods);
            this.rootStore.orderEditViewStore.setRestaurant(restaurant);

            this.rootStore.orderEditViewStore.updateDeliveryFee();
            this.rootStore.orderEditViewStore.updateTotalAmount();

            this.setIsOrderDetailsDrawerOpen(false);
            this.rootStore.orderEditViewStore.setIsOrderCartDrawerOpen(true);
        })
    }

    orderRepeatContainer: OrderRepeatContainer | undefined;
    setOrderRepeatContainer = (orderRepeatContainer: OrderRepeatContainer | undefined) => {
        this.orderRepeatContainer = orderRepeatContainer;
    }

    isOrderDetailsDrawerOpen: boolean;
    setIsOrderDetailsDrawerOpen = (value: boolean) => {
        runInAction(() => {
            if (value === false) {
                this.setOrderRepeatContainer(undefined);
            }

            this.isOrderDetailsDrawerOpen = value;
        })
    }
}